import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail, AlertCircle, CheckCircle, ArrowLeft, KeyRound, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { adminApi } from '../lib/api';
import { toast } from 'sonner';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Password change state (for forced change)
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({ 
    currentPassword: '', 
    newPassword: '', 
    confirmPassword: '' 
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Password reset state
  const [showResetFlow, setShowResetFlow] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [securityCode, setSecurityCode] = useState('');
  const [newResetPassword, setNewResetPassword] = useState('');
  const [confirmResetPassword, setConfirmResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('admin_token');
    const mustChange = localStorage.getItem('must_change_password');
    
    if (token && mustChange === 'true') {
      setShowPasswordChange(true);
    } else if (token) {
      navigate('/admin/dashboard');
    }
    
    // Initialize default admin on first load
    adminApi.init().catch(() => {});
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await adminApi.login(credentials);
      localStorage.setItem('admin_token', res.data.token);
      localStorage.setItem('admin_name', res.data.name);
      localStorage.setItem('admin_role', res.data.role || 'admin');
      
      // Check if password change is required
      if (res.data.must_change_password) {
        localStorage.setItem('must_change_password', 'true');
        setPasswordData({ ...passwordData, currentPassword: credentials.password });
        setShowPasswordChange(true);
        toast.info('Please change your password to continue');
      } else {
        localStorage.removeItem('must_change_password');
        toast.success('Login successful');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.currentPassword === passwordData.newPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setChangingPassword(true);

    try {
      await adminApi.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      
      localStorage.removeItem('must_change_password');
      toast.success('Password changed successfully!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Direct password reset handler
  const handleDirectReset = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    if (!securityCode) {
      toast.error('Please enter the security code');
      return;
    }
    
    if (newResetPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    if (newResetPassword !== confirmResetPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setResetting(true);
    try {
      await adminApi.resetPasswordDirect({
        email: resetEmail,
        security_code: securityCode,
        new_password: newResetPassword
      });
      toast.success('Password reset successfully!');
      setResetSuccess(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed. Check your email and security code.');
    } finally {
      setResetting(false);
    }
  };

  const resetToLogin = () => {
    setShowResetFlow(false);
    setResetEmail('');
    setSecurityCode('');
    setNewResetPassword('');
    setConfirmResetPassword('');
    setResetSuccess(false);
  };

  // Password Reset Flow UI
  if (showResetFlow) {
    return (
      <div className="min-h-screen bg-[#FDFCFA] flex items-center justify-center px-6" data-testid="password-reset-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#C2A46D]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-[#C2A46D]" />
            </div>
            <h1 className="text-3xl font-serif text-[#222222] mb-2">Reset Password</h1>
            <p className="text-[#555555]">
              {resetSuccess ? "Your password has been reset" : "Enter your details to reset your password"}
            </p>
          </div>

          <div className="bg-white p-8 border border-[#E8E4DC] shadow-lg">
            {resetSuccess ? (
              // Success State
              <div className="text-center space-y-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-[#222222] font-medium mb-2">Password Reset Complete!</p>
                  <p className="text-[#555555] text-sm">You can now log in with your new password.</p>
                </div>
                <Button
                  onClick={resetToLogin}
                  className="w-full bg-[#1F2A44] hover:bg-[#151D30] text-white py-3 rounded-sm"
                  data-testid="back-to-login-btn"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              // Reset Form
              <form onSubmit={handleDirectReset} className="space-y-5">
                <div>
                  <Label htmlFor="resetEmail" className="text-[#222222]">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#555555]" />
                    <Input
                      id="resetEmail"
                      type="email"
                      required
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="pl-10 border-[#E8E4DC] focus:border-[#1F2A44] rounded-sm"
                      placeholder="Enter your admin email"
                      data-testid="reset-email"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="securityCode" className="text-[#222222]">Security Code</Label>
                  <div className="relative mt-1">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#555555]" />
                    <Input
                      id="securityCode"
                      type="password"
                      required
                      value={securityCode}
                      onChange={(e) => setSecurityCode(e.target.value)}
                      className="pl-10 border-[#E8E4DC] focus:border-[#1F2A44] rounded-sm"
                      placeholder="Enter security code"
                      data-testid="security-code"
                    />
                  </div>
                  <p className="text-xs text-[#777777] mt-1">Contact the site administrator if you don't have the security code.</p>
                </div>

                <div>
                  <Label htmlFor="newResetPassword" className="text-[#222222]">New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#555555]" />
                    <Input
                      id="newResetPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      required
                      value={newResetPassword}
                      onChange={(e) => setNewResetPassword(e.target.value)}
                      className="pl-10 pr-10 border-[#E8E4DC] focus:border-[#1F2A44] rounded-sm"
                      placeholder="Min 8 characters"
                      data-testid="new-reset-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555555] hover:text-[#222222]"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {newResetPassword && (
                    <div className="mt-2 flex items-center gap-2">
                      {newResetPassword.length >= 8 ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      )}
                      <span className={`text-xs ${newResetPassword.length >= 8 ? 'text-green-600' : 'text-red-500'}`}>
                        {newResetPassword.length >= 8 ? 'Password meets minimum length' : 'Must be at least 8 characters'}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="confirmResetPassword" className="text-[#222222]">Confirm New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#555555]" />
                    <Input
                      id="confirmResetPassword"
                      type="password"
                      required
                      value={confirmResetPassword}
                      onChange={(e) => setConfirmResetPassword(e.target.value)}
                      className="pl-10 border-[#E8E4DC] focus:border-[#1F2A44] rounded-sm"
                      placeholder="Confirm new password"
                      data-testid="confirm-reset-password"
                    />
                  </div>
                  {confirmResetPassword && newResetPassword && (
                    <div className="mt-2 flex items-center gap-2">
                      {confirmResetPassword === newResetPassword ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      )}
                      <span className={`text-xs ${confirmResetPassword === newResetPassword ? 'text-green-600' : 'text-red-500'}`}>
                        {confirmResetPassword === newResetPassword ? 'Passwords match' : 'Passwords do not match'}
                      </span>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={resetting || newResetPassword.length < 8 || newResetPassword !== confirmResetPassword}
                  className="w-full bg-[#1F2A44] hover:bg-[#151D30] text-white py-3 rounded-sm"
                  data-testid="reset-password-btn"
                >
                  {resetting ? 'Resetting...' : 'Reset Password'}
                </Button>

                <button
                  type="button"
                  onClick={resetToLogin}
                  className="w-full text-center text-[#555555] hover:text-[#1F2A44] text-sm flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Password change form (forced change)
  if (showPasswordChange) {
    return (
      <div className="min-h-screen bg-[#FDFCFA] flex items-center justify-center px-6" data-testid="password-change-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#C2A46D]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#C2A46D]" />
            </div>
            <h1 className="text-3xl font-serif text-[#222222] mb-2">Change Password</h1>
            <p className="text-[#555555]">Please set a new secure password to continue</p>
          </div>

          <div className="bg-white p-8 border border-[#E8E4DC] shadow-lg">
            <div className="bg-[#FFF9E6] border border-[#C2A46D] p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#C2A46D] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#555555]">
                For security reasons, you must change your password before accessing the admin panel.
              </p>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-5">
              <div>
                <Label htmlFor="currentPassword" className="text-[#222222]">Current Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#555555]" />
                  <Input
                    id="currentPassword"
                    type="password"
                    required
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    className="pl-10 border-[#E8E4DC] focus:border-[#1F2A44] rounded-sm"
                    placeholder="Enter current password"
                    data-testid="current-password"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="newPassword" className="text-[#222222]">New Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#555555]" />
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    required
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className="pl-10 pr-10 border-[#E8E4DC] focus:border-[#1F2A44] rounded-sm"
                    placeholder="Enter new password (min 8 characters)"
                    data-testid="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555555] hover:text-[#222222]"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {passwordData.newPassword && (
                  <div className="mt-2 flex items-center gap-2">
                    {passwordData.newPassword.length >= 8 ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-xs ${passwordData.newPassword.length >= 8 ? 'text-green-600' : 'text-red-500'}`}>
                      {passwordData.newPassword.length >= 8 ? 'Password meets minimum length' : 'Must be at least 8 characters'}
                    </span>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-[#222222]">Confirm New Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#555555]" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    required
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    className="pl-10 border-[#E8E4DC] focus:border-[#1F2A44] rounded-sm"
                    placeholder="Confirm new password"
                    data-testid="confirm-password"
                  />
                </div>
                {passwordData.confirmPassword && passwordData.newPassword && (
                  <div className="mt-2 flex items-center gap-2">
                    {passwordData.confirmPassword === passwordData.newPassword ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-xs ${passwordData.confirmPassword === passwordData.newPassword ? 'text-green-600' : 'text-red-500'}`}>
                      {passwordData.confirmPassword === passwordData.newPassword ? 'Passwords match' : 'Passwords do not match'}
                    </span>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={changingPassword || passwordData.newPassword.length < 8 || passwordData.newPassword !== passwordData.confirmPassword}
                className="w-full bg-[#1F2A44] hover:bg-[#151D30] text-white py-3 rounded-sm"
                data-testid="change-password-submit"
              >
                {changingPassword ? 'Changing Password...' : 'Change Password & Continue'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Main Login Form
  return (
    <div className="min-h-screen bg-[#FDFCFA] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-serif text-[#222222] mb-2">Admin Login</h1>
          <p className="text-[#555555]">J. Brantley Law, PLLC</p>
        </div>

        <div className="bg-white p-8 border border-[#E8E4DC] shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-[#222222]">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#555555]" />
                <Input
                  id="email"
                  type="email"
                  required
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  className="pl-10 border-[#E8E4DC] focus:border-[#1F2A44] rounded-sm"
                  placeholder="Enter your email"
                  data-testid="admin-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-[#222222]">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#555555]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="pl-10 pr-10 border-[#E8E4DC] focus:border-[#1F2A44] rounded-sm"
                  placeholder="••••••••"
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555555] hover:text-[#222222]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1F2A44] hover:bg-[#151D30] text-white py-3 rounded-sm"
              data-testid="admin-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Forgot Password Link */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setShowResetFlow(true)}
              className="text-sm text-[#555555] hover:text-[#1F2A44] underline underline-offset-2"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
